import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Button, Col, Row, Form, FormGroup, Input, Label } from 'reactstrap';
import InfoValueCard from '../../shared/InfoValueCard';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner, faList } from '@fortawesome/free-solid-svg-icons';
import GridContainer from '../../shared/GridContainer';
import { get ,put } from '../../../helpers/requests';
import { API, BASE_URL, FRONT_MF, LIBRO_VENTAS_DIA_LIMITE_CARGA, ROLES_ASEGURADO } from '../../../CONST';
import { vigenciasToOptions } from '../../../helpers/selects';
import {
  cellDateOperation,
  cellNumOperation,
  cellOperation,
  headerDoubleRowOperation,
  headerOperation,
} from '../../../helpers/tables';
import useTableFilters from '../../shared/useTableFilters';
import BaseTable from '../../shared/BaseTable';
import { fileDownload, intermediaryAccess, userHasRole } from '../../../helpers/helpers';
import FileUploader from '../../shared/FileUploader';
import DownloadBtn from '../../shared/DownloadBtn';
import SiniestrosModal from '../../corredor/credito/SiniestrosModal';

const LibrosVenta = ({ policyId, client }) => {
  const [loadingDownload, setLoadingDownload] = useState(null);
  const [docExistsCache, setDocExistsCache] = useState({});
  const loginIdToken = useSelector((state) => state.login.id_token);
  const [poliza, setPoliza] = useState({ nemotecnico: '', moneda: '' });
  const [vigencias, setVigencias] = useState({ all: [], selected: null });
  const [documentsData, setDocumentsData] = useState({});
  const location = useLocation();
  const vigenciaFromState = location.state?.vigencia;
  const librosFilterParams = `poliza_id=${policyId}&vigencia=${
    vigencias.selected ? vigencias.selected.numero_vigencia : -1
  }`;
  const [onFilteredChange, onFetchData, loading, data] = useTableFilters(
    `${BASE_URL}${API.librosVenta}/`,
    !vigencias.selected,
    client ? `${librosFilterParams}&client_id=${client.id}` : librosFilterParams,
  );
  const tableRef = useRef(null);
  const user = useSelector((state) => state.login.user);

  const allVigenciasOpts = vigenciasToOptions(vigencias.all);
  const selectedVigenciaOpt = allVigenciasOpts.find(
    (opt) => opt.value.id === vigencias.selected.id,
  );

  const [selectedEndoso, setSelectedEndoso] = useState(null);
  const [siniestrosData, setSiniestrosData] = useState({ count: 0, monto: 0 });
  const [siniestrosRaw, setSiniestrosRaw] = useState([]);
  const [showSiniestrosModal, setShowSiniestrosModal] = useState(false);


  useEffect(() => {
    if (!vigencias.selected || !poliza.nemotecnico) return;

    const fetchSiniestros = async () => {
      try {
        const url = `${BASE_URL}${API.siniestros}/?poliza=${poliza.nemotecnico}&estado=PAGADO`;
        const response = await get(url);
        const siniestros = response.results || [];

        const vigenciaNum = vigencias.selected.numero_vigencia;

        let count = 0;
        let monto = 0;
        const raw = [];

        siniestros.forEach(s => {
          if ((s.vigencias || []).includes(vigenciaNum)) {
            count += 1;
            monto += parseFloat(s.monto_pagado || 0);
            raw.push(s);
          }
        });

        setSiniestrosData({ count, monto });
        setSiniestrosRaw(raw);
      } catch (error) {
        console.error('Error fetching siniestros:', error);
      }
    };

    fetchSiniestros();
  }, [vigencias.selected, poliza.nemotecnico]);

  useEffect(() => {
    if (!vigencias.selected) return;

    const endosoUrl = `${BASE_URL}${API.simpleEndososCredito}/?poliza_id=${policyId}&numero_vigencia=${vigencias.selected.numero_vigencia}`;
    get(client ? `${endosoUrl}&client_id=${client.id}` : endosoUrl).then((resp) => {
      if (resp && resp.length > 0) {
        setSelectedEndoso(resp[0]);
      }
    });
  }, [vigencias.selected, policyId, client]);

  useEffect(() => {
    // Buscamos la póliza
    const creditUrl = `${BASE_URL}${API.creditos}/${policyId}`;
    get(client ? `${creditUrl}?client_id=${client.id}` : creditUrl).then((resp) => setPoliza(resp));
    // Buscamos las vigencias
    const vigenciasUrl = `${BASE_URL}${API.simpleEndososCredito}/?poliza_id=${policyId}`;
    get(client ? `${vigenciasUrl}&client_id=${client.id}` : vigenciasUrl).then((resp) => {
      const matchedVigencia = vigenciaFromState
        ? resp.find((v) => v.numero_vigencia === vigenciaFromState)
        : null;
 
      setVigencias({
        all: resp,
        selected: matchedVigencia || (resp.length > 0 ? resp[0] : null),
      });
    });
  }, [policyId, client]);
 

  useEffect(() => {
    onFetchData(tableRef.current.state);
  }, [vigencias.selected, client]);

  const handleVigenciaChange = (selectedOption) => {
    setVigencias((prevState) => ({
      ...prevState,
      selected: selectedOption.value,
    }));
  };

  const handleLibroUploaded = () => {
    onFetchData(tableRef.current.state);
  };



  const fetchTableData = async () => {
    try {
      if (!data || !data.data) return;

      const periods = data.data.map(row => row.periodo);
      const periodParams = periods.map(p => `periodo=${p}`).join('&');
      const url = `${BASE_URL}${API.documentoTributario}/table_data/?poliza_id=${policyId}&${periodParams}`;

      const response = await get(url);
      setDocumentsData(response);

    } catch (error) {
      console.error('Error fetching table data:', error);
    }
  };

  useEffect(() => {
    fetchTableData();
  }, [data, policyId]);

  const hasDocumentoTributario = (periodo) => {
    return documentsData[periodo]?.hasDocuments || false;
  };

  const handleDownload = async (periodo) => {
    if (!hasDocumentoTributario(periodo)) {
      console.warn('No DocumentoTributario found for period:', periodo);
      return;
    }

    setLoadingDownload(periodo);
    try {
      const downloadUrl = `${BASE_URL}${API.documentoTributario}/download_by_periodo/?poliza_id=${policyId}&periodo=${periodo}`;

      await new Promise((resolve, reject) => {
        fileDownload(
          downloadUrl, 
          loginIdToken, 
          () => {
            resolve();
          }
        );
      });

    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setLoadingDownload(null);
    }
  };

  const calculatePrimaReal = () => {
    if (!data?.data || data.data.length === 0) return 0;

    return data.data.reduce((total, row) => {
      return total + (row.prima_neta_generada_mo || 0);
    }, 0);
  };

  const calculatePrimaMinima = () => {
    const primaMinima = selectedEndoso?.prima_minima_anual_amt;
    if (!primaMinima || typeof primaMinima !== 'number') return 0;
    const periodsWithData = data?.data?.filter(
      row => row.prima_neta_generada_mo !== null && row.prima_neta_generada_mo !== undefined
    ).length || 0;

    if (periodsWithData === 0) return 0;

    return (primaMinima / 12) * periodsWithData;
  };

  const calculateComisionGenerada = () => {
    const primaReal = calculatePrimaReal();
    const comisionPct = selectedEndoso?.comision_pct;
    if (!comisionPct) return 0;

    return primaReal * (comisionPct / 100);
  };

  const calculateSiniestralidad = () => {
    const primaReal = calculatePrimaReal();
    const primaMinima = calculatePrimaMinima();
    const denominator = Math.max(primaReal, primaMinima);
    if (denominator === 0) return 0;
    return (siniestrosData.monto / denominator) * 100;
  };

  const columns = [
    {
      Header: headerDoubleRowOperation('Período', 'de Ventas'),
      id: 'periodo',
      accessor: 'periodo',
      Cell: (row) => cellDateOperation(row.value, FRONT_MF),
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerDoubleRowOperation('Archivo', 'de Carga (*)'),
      id: 'archivo',
      Cell: (row) => {
        const limitDate = moment(row.original.periodo)
          .add(1, 'months')
          .date(LIBRO_VENTAS_DIA_LIMITE_CARGA);
        const beforeLimitDate = moment().isSameOrBefore(limitDate, 'date');
        return cellOperation(
          <>
            {!intermediaryAccess(user) && (
              <FileUploader
                inputId={`libro-file-${row.original.id}`}
                type="link"
                uploadUrl={`${BASE_URL}${API.librosVenta}/${row.original.id}/`}
                preUploadUrl={`${BASE_URL}${API.uploadLibroVenta}/${row.original.id}/?PeriodDate=${row.original.periodo}`}
                accept=".xls,.xlsx"
                disabled={!beforeLimitDate && !!row.original.libro_venta_id && row.original.estado.id !== 4}
                handleResponse={handleLibroUploaded}
                stateData={row.original.estado}
                confirmButtonText="Continuar"
              />
            )}
            <button
              type="button"
              className="table-link"
              style={{ fontSize: '18px', marginLeft: '10px' }}
              onClick={() =>
                fileDownload(
                  `${BASE_URL}${API.librosVenta}/${row.original.id}/download`,
                  loginIdToken,
                )
              }
              title="descargar"
              disabled={!row.original.libro_venta_id}
            >
              <FontAwesomeIcon icon={faDownload} />
            </button>
          </>,
        );
      },
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerDoubleRowOperation('Estado', 'de Carga'),
      id: 'estado',
      accessor: 'estado',
      Cell: (row) => cellOperation(row.value.nombre),
      sortable: false,
      minWidth: 120,
    },
    {
      Header: headerDoubleRowOperation('Libro de Ventas', 'Procesado'),
      id: 'libro_ventas_procesado',
      Cell: (row) => {
        const hasDocTributario = hasDocumentoTributario(row.original.periodo);
        const isDownloading = loadingDownload === row.original.periodo;
        
        return cellOperation(
          <button
            type="button"
            className="table-link"
            style={{ fontSize: '18px' }}
            onClick={() => handleDownload(row.original.periodo)}
            title={
              hasDocTributario 
                ? "Descargar documentos tributarios procesados"
                : "No hay documentos tributarios procesados para este período"
            }
            disabled={!hasDocTributario || isDownloading}
          >
            {isDownloading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon 
                icon={faDownload} 
                style={{ opacity: hasDocTributario ? 1 : 0.3 }}
              />
            )}
          </button>,
        );
      },
      sortable: false,
      minWidth: 110,
    },
    {
      Header: headerOperation('Documentos Declarados'),
      columns: [
        {
          Header: headerOperation('Cantidad'),
          id: 'declarados_cantidad',
          accessor: 'declarados_cantidad',
          Cell: (row) => cellNumOperation(row.value),
          sortable: false,
          minWidth: 100,
        },
        {
          Header: headerDoubleRowOperation('Monto Venta', 'Bruta (mm$)'),
          id: 'declarados_monto',
          accessor: 'declarados_monto',
          Cell: (row) => cellNumOperation(row.value),
          sortable: false,
          minWidth: 100,
        },
      ],
    },
    {
      Header: headerOperation('Documentos No Considerados'),
      columns: [
        {
          Header: headerOperation('Cantidad'),
          id: 'no_considerados_cantidad',
          accessor: 'no_considerados_cantidad',
          Cell: (row) => cellNumOperation(row.value),
          sortable: false,
          minWidth: 100,
        },
        {
          Header: headerDoubleRowOperation('Monto Venta', 'Bruta (mm$)'),
          id: 'no_considerados_monto',
          accessor: 'no_considerados_monto',
          Cell: (row) => cellNumOperation(row.value),
          sortable: false,
          minWidth: 100,
        },
      ],
    },
    {
      Header: headerOperation('Documentos Considerados'),
      columns: [
        {
          Header: headerOperation('Cantidad'),
          id: 'considerados_cantidad',
          accessor: 'considerados_cantidad',
          Cell: (row) => cellNumOperation(row.value),
          sortable: false,
          minWidth: 100,
        },
        {
          Header: headerDoubleRowOperation('Monto Venta', 'Bruta (mm$)'),
          id: 'considerados_monto',
          accessor: 'considerados_monto',
          Cell: (row) => cellNumOperation(row.value),
          sortable: false,
          minWidth: 100,
        },
      ],
    },
    {
      Header: headerDoubleRowOperation('Prima Neta', 'Generada (MO)'),
      id: 'prima_neta_generada_mo',
      accessor: 'prima_neta_generada_mo',
      Cell: (row) => cellNumOperation(row.value, 'float'),
      sortable: false,
      minWidth: 100,
    },
  ];

  /* USANDO EL DOCUMENTO TRIBUTARIO ÚNICAMENTE. Algún día se usará en lugar del otro...
  const columns = [
    {
      Header: headerDoubleRowOperation('Período', 'de Ventas'),
      id: 'periodo',
      accessor: 'periodo',
      Cell: (row) => cellDateOperation(row.value, FRONT_MF),
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerDoubleRowOperation('Archivo', 'de Carga (*)'),
      id: 'archivo',
      Cell: (row) => {
        if (intermediaryAccess(user)) {
          return cellOperation("-");
        }
      
        const limitDate = moment(row.original.periodo)
          .add(1, 'months')
          .date(LIBRO_VENTAS_DIA_LIMITE_CARGA);
        const beforeLimitDate = moment().isSameOrBefore(limitDate, 'date');
        return cellOperation(
          <>
            {!intermediaryAccess(user) && (
              <FileUploader
                inputId={`libro-file-${row.original.id}`}
                type="link"
                uploadUrl={`${BASE_URL}${API.librosVenta}/${row.original.id}/`}
                preUploadUrl={`${BASE_URL}${API.uploadLibroVenta}/${row.original.id}/?PeriodDate=${row.original.periodo}`}
                accept=".xls,.xlsx"
                disabled={!beforeLimitDate && !!row.original.libro_venta_id && row.original.estado.id !== 4}
                handleResponse={handleLibroUploaded}
                stateData={row.original.estado}
                confirmButtonText="Continuar"
              />
            )}
            {!intermediaryAccess(user) && (
            <button
              type="button"
              className="table-link"
              style={{ fontSize: '18px', marginLeft: '10px' }}
              onClick={() =>
                fileDownload(
                  `${BASE_URL}${API.librosVenta}/${row.original.id}/download`,
                  loginIdToken,
                )
              }
              title="descargar"
              disabled={!row.original.libro_venta_id}
            >
              
              <FontAwesomeIcon icon={faDownload} />
            </button>
            )}
          </>,
        );
      },
      sortable: false,
      minWidth: 100,
    },
    {
      Header: headerDoubleRowOperation('Estado', 'de Carga'),
      id: 'estado',
      accessor: 'estado',
      Cell: (row) => cellOperation(row.value.nombre),
      sortable: false,
      minWidth: 120,
    },
    {
      Header: headerDoubleRowOperation('Libro de Ventas', 'Procesado'),
      id: 'libro_ventas_procesado',
      Cell: (row) => {
        if (intermediaryAccess(user)) {
          return cellOperation("-");
        }
        const hasDocTributario = hasDocumentoTributario(row.original.periodo);
        const isDownloading = loadingDownload === row.original.periodo;
        
        return cellOperation(
          <button
            type="button"
            className="table-link"
            style={{ fontSize: '18px' }}
            onClick={() => handleDownload(row.original.periodo)}
            title={
              hasDocTributario 
                ? "Descargar documentos tributarios procesados"
                : "No hay documentos tributarios procesados para este período"
            }
            disabled={!hasDocTributario || isDownloading}
          >
            {isDownloading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon 
                icon={faDownload} 
                style={{ opacity: hasDocTributario ? 1 : 0.3 }}
              />
            )}
          </button>,
        );
      },
      sortable: false,
      minWidth: 110,
    },
    {
      Header: headerOperation('Documentos Declarados'),
      columns: [
        {
          Header: headerOperation('Cantidad'),
          id: 'declarados_cantidad',
          accessor: 'declarados_cantidad',
          Cell: (row) => cellNumOperation(documentsData[row.original.periodo]?.declarados_cantidad || 0),
          sortable: false,
          minWidth: 100,
        },
        {
          Header: headerDoubleRowOperation('Monto Venta', 'Bruta (mm$)'),
          id: 'declarados_monto',
          accessor: 'declarados_monto',
          Cell: (row) => cellNumOperation(documentsData[row.original.periodo]?.declarados_monto || 0),
          sortable: false,
          minWidth: 100,
        },
        {
          Header: headerDoubleRowOperation('Prima Neta', 'Generada (MO)'),
          id: 'declarados_prima',
          accessor: 'declarados_prima',
          Cell: (row) => cellNumOperation(documentsData[row.original.periodo]?.declarados_prima || 0, 'float'),
          sortable: false,
          minWidth: 100,
        },
      ],
    },
    {
      Header: headerOperation('Documentos Considerados'),
      columns: [
        {
          Header: headerOperation('Cantidad'),
          id: 'considerados_cantidad',
          accessor: 'considerados_cantidad',
          Cell: (row) => cellNumOperation(documentsData[row.original.periodo]?.considerados_cantidad || 0),
          sortable: false,
          minWidth: 100,
        },
        {
          Header: headerDoubleRowOperation('Monto Venta', 'Bruta (mm$)'),
          id: 'considerados_monto',
          accessor: 'considerados_monto',
          Cell: (row) => cellNumOperation(documentsData[row.original.periodo]?.considerados_monto || 0),
          sortable: false,
          minWidth: 100,
        },
        {
          Header: headerDoubleRowOperation('Prima Neta', 'Generada (MO)'),
          id: 'considerados_prima',
          accessor: 'considerados_prima',
          Cell: (row) => cellNumOperation(documentsData[row.original.periodo]?.considerados_prima || 0, 'float'),
          sortable: false,
          minWidth: 100,
        },
      ],
    },
    {
      Header: headerOperation('Documentos No Considerados'),
      columns: [
        {
          Header: headerOperation('Cantidad'),
          id: 'no_considerados_cantidad',
          accessor: 'no_considerados_cantidad',
          Cell: (row) => cellNumOperation(documentsData[row.original.periodo]?.no_considerados_cantidad || 0),
          sortable: false,
          minWidth: 100,
        },
        {
          Header: headerDoubleRowOperation('Monto Venta', 'Bruta (mm$)'),
          id: 'no_considerados_monto',
          accessor: 'no_considerados_monto',
          Cell: (row) => cellNumOperation(documentsData[row.original.periodo]?.no_considerados_monto || 0),
          sortable: false,
          minWidth: 100,
        },
        {
          Header: headerDoubleRowOperation('Prima Neta', 'Generada (MO)'),
          id: 'no_considerados_prima',
          accessor: 'no_considerados_prima',
          Cell: (row) => cellNumOperation(documentsData[row.original.periodo]?.no_considerados_prima || 0, 'float'),
          sortable: false,
          minWidth: 100,
        },
      ],
    },
  ];
  */

  const xlsxUrl = vigencias.selected
    ? `${BASE_URL}${API.librosVenta}/download_xlsx/?${librosFilterParams}`
    : '';
  const finalXlsxUrl = client ? `${xlsxUrl}&client_id=${client.id}` : xlsxUrl;
  const pdfUrl = vigencias.selected
    ? `${BASE_URL}${API.librosVenta}/download_pdf/?${librosFilterParams}`
    : '';
  const finalPdfUrl = client ? `${pdfUrl}&client_id=${client.id}` : pdfUrl;

  return (
    <div>
      <Col md={{ size: 12 }}>
        <div style={{ padding: '0 0 50px 0' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '450px auto',
            gridGap: '16px',
            alignItems: 'start',
          }}>
            {/* Left side - Form fields */}
            <Form>
              <FormGroup row className="mb-0">
                <Label for="poliza" sm={3} className="orsan-label">
                  Póliza
                </Label>
                <Col sm={7}>
                  <Input
                    id="poliza"
                    plaintext
                    disabled
                    value={poliza.nemotecnico}
                    onChange={() => {}}
                  />
                </Col>
              </FormGroup>
              <FormGroup row className="mb-0">
                <Label for="poliza" sm={3} className="orsan-label">
                  Moneda Origen (MO)
                </Label>
                <Col sm={7}>
                  <Input id="poliza" plaintext disabled value={poliza.moneda} onChange={() => {}} />
                </Col>
              </FormGroup>
              <FormGroup row className="mb-0">
                <Label for="vigencia" sm={3} className="orsan-label">
                  Vigencia
                </Label>
                <Col sm={7}>
                  <Select
                    id="vigencia"
                    name="vigencia"
                    className="orsan-form-element"
                    value={selectedVigenciaOpt}
                    onChange={handleVigenciaChange}
                    options={allVigenciasOpts}
                    placeholder="Seleccione vigencia"
                  />
                </Col>
              </FormGroup>
            </Form>

            {/* Right side - Summary cards */}
            <Row
              style={{ marginTop: '0px', gap: '12px' }}
              className="align-items-stretch"
              noGutters
            >
              <Col className="d-flex flex-column">
                <InfoValueCard
                  title="Prima Real"
                  loading={loading}
                  amount={calculatePrimaReal()}
                  moneda={poliza.moneda}
                  color="var(--orsan-blue)"
                  className="h-100"
                />
              </Col>
              <Col className="d-flex flex-column">
                <InfoValueCard
                  title="Prima Mínima"
                  loading={loading}
                  amount={calculatePrimaMinima()}
                  secondAmount={selectedEndoso?.prima_minima_anual_amt ?? undefined}
                  secondAmountLabel="Anual"
                  moneda={poliza.moneda}
                  color="var(--orsan-green)"
                  className="h-100"
                />
              </Col>
              <Col className="d-flex flex-column">
                <InfoValueCard
                  title="Siniestros Pagados"
                  loading={loading}
                  amount={siniestrosData.monto}
                  moneda={poliza.moneda}
                  color="var(--orsan-red)"
                  className="h-100"
                />
              </Col>
              <Col className="d-flex flex-column">
                <div
                  style={{ cursor: 'pointer', position: 'relative' }}
                  onClick={() => setShowSiniestrosModal(true)}
                  title="Ver detalle de siniestros"
                >
                  <InfoValueCard
                    title="Siniestralidad"
                    loading={loading}
                    amount={calculateSiniestralidad()}
                    showMoneda={false}
                    amountLabel="Ratio %"
                    color="var(--orsan-orange)"
                    className="h-100"
                  />
                  <span style={{ position: 'absolute', top: 8, right: 10, color: 'var(--orsan-blue)', fontSize: 13, opacity: 0.7 }}>
                    <FontAwesomeIcon icon={faList} /> Ver detalle
                  </span>
                </div>
              </Col>
              {(!userHasRole(user, ROLES_ASEGURADO) || user.is_orsan) &&(
                <Col className="d-flex flex-column">
                  <InfoValueCard
                    title="Comisión Generada"
                    loading={loading}
                    amount={calculateComisionGenerada()}
                    moneda={poliza.moneda}
                    color="var(--orsan-blue-s3)"
                  />
                </Col>
              )}
            </Row>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto auto auto',
            gridGap: '10px',
            marginBottom: '6px',
          }}>
            <div style={{ gridColumnStart: 2 }}>
              {!intermediaryAccess(user) && (
                <DownloadBtn
                  id="plantilla"
                  text="PLANTILLA"
                  tipText="Descargar libro de ventas de muestra"
                  url="/files/Plantilla_Formulario_De_Declaración_De_Ventas.xlsx"
                />
              )}
            </div>
            <div style={{ gridColumnStart: 3 }}>
              <DownloadBtn
                id="xlsx-download"
                text="XLSX"
                tipText="Descargar planilla excel"
                url={xlsxUrl ? finalXlsxUrl : ''}
              />
            </div>
            <div style={{ gridColumnStart: 4 }}>
              <DownloadBtn
                id="pdf-download"
                text="PDF"
                tipText="Descargar PDF"
                url={pdfUrl ? finalPdfUrl : ''}
              />
            </div>
          </div>

          <BaseTable
            tableRef={tableRef}
            columns={columns}
            showPagination
            style={{ maxHeight: '63vh' }}
            data={data.data}
            pages={data.pages}
            pageSize={13}
            resultCount={data.count}
            onFetchData={onFetchData}
            onFilteredChange={onFilteredChange}
            loading={loading}
            showBorders
          />

          <Link to="/asegurado/polizas-credito">
            <Button
              className="float-right"
              color="orsan-secondary"
              style={{ width: '150px', fontSize: '14px', marginTop: '10px' }}
            >
              Volver
            </Button>
          </Link>
        </div>
      </Col>
      <SiniestrosModal
        isOpen={showSiniestrosModal}
        onClose={() => setShowSiniestrosModal(false)}
        siniestros={siniestrosRaw}
        moneda={poliza.moneda}
      />
    </div>
  );
};

LibrosVenta.propTypes = {
  policyId: PropTypes.string.isRequired,
  client: PropTypes.shape({
    id: PropTypes.number,
  }),
};

LibrosVenta.defaultProps = {
  client: null,
};

export default LibrosVenta;
